import { Avatar, Tooltip } from "@mui/material";

/**
 * Circular avatar with the user's initials and a deterministic background color
 * derived from their email/user_id. Pattern matches Notion / Linear / GitHub.
 *
 * Props:
 *   user: { display_name?, email?, user_id?, is_admin? }
 *   size: number (default 24)
 *   tooltip: bool (default true) — show name + email on hover
 *   sx: passthrough
 */
export default function UserAvatar({ user, size = 24, tooltip = true, sx = {}, ...rest }) {
  const label = user?.display_name || user?.email || user?.user_id || "?";
  const seed = (user?.email || user?.user_id || label).toLowerCase();
  const bg = colorFromSeed(seed);
  const fg = contrastTextOn(bg);

  const initials = computeInitials(user?.display_name || user?.email || user?.user_id || "?");

  const tooltipText = (() => {
    const parts = [];
    if (user?.display_name) parts.push(user.display_name);
    if (user?.email && user.email !== user?.display_name) parts.push(user.email);
    if (user?.is_admin) parts.push("(admin)");
    return parts.join(" · ");
  })();

  const avatar = (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: Math.max(10, Math.floor(size * 0.42)),
        bgcolor: bg,
        color: fg,
        fontWeight: 600,
        border: "2px solid white",
        ...sx,
      }}
      {...rest}
    >
      {initials}
    </Avatar>
  );

  return tooltip ? <Tooltip title={tooltipText}>{avatar}</Tooltip> : avatar;
}

function computeInitials(s) {
  s = (s || "").trim();
  if (!s) return "?";
  // If it looks like an email, use the first letter of the local part + first letter
  // of the domain.
  if (s.includes("@")) {
    const [local] = s.split("@");
    return (local.match(/[A-Za-z]/g) || ["?"])[0].toUpperCase();
  }
  // Use first letter of each word, max 2
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

// Deterministic palette derived from a stable hash of the seed string
function colorFromSeed(seed) {
  // simple djb2 hash → hue
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  // Mid-saturation, mid-lightness for readable contrast
  return `hsl(${hue}, 55%, 48%)`;
}

function contrastTextOn(hslStr) {
  // Trivial: all our bg colors use 48% lightness which is dark enough for white text.
  // Kept as a separate function to make later tweaks (e.g., light themed avatars) easy.
  return "white";
}
